/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   HeaderMobileSingleInput
 */

import { HamburgerCloseButton } from '@zcode-ui/controls'
import { getAIAssistantSearchFilters, useAskZoe } from '@zcode-ui/filters'
import { ZumperFullDark } from '@zcode-ui/icons'
import { Button, cn } from '@zcode-ui/shadz-ui'
import { ZoeHeaderModal } from '@zcode-ui/web-zumper-features'
import type { PassedHeaderProps } from '../../../types'
import { SaveSearchButtonMobileContainer } from '../../SaveSearchButton'

interface HeaderMobileSingleInputProps {
  applyStickyStyles?: boolean
  handleHamburgerClick: () => void
  location?: string
}

export const HeaderMobileSingleInput = ({
  applyStickyStyles,
  dispatchCreateSearch,
  dispatchMixpanelAutocompleteResultClick,
  dispatchRemoveSavedSearch,
  handleHamburgerClick,
  location,
  mixpanelEvent,
  onSaveSearchModalOpen,
  onSearchButtonClick,
  onSelectAutocompleteResult,
  url,
}: HeaderMobileSingleInputProps &
  Pick<
    PassedHeaderProps,
    | 'dispatchCreateSearch'
    | 'dispatchMixpanelAutocompleteResultClick'
    | 'dispatchRemoveSavedSearch'
    | 'onSaveSearchModalOpen'
    | 'onSearchButtonClick'
    | 'onSelectAutocompleteResult'
    | 'mixpanelEvent'
    | 'url'
  >) => {
  const locationString = location ?? 'Where do you want to stay?'
  const variation = useAskZoe(true)
  const showZoe = variation === 'SHOW_ZOE' || variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT'

  return (
    <nav
      className={cn(
        applyStickyStyles ? 'fixed' : 'absolute',
        'h-[86px] top-0 right-0 left-0 px-6 bg-gray-50 border-b border-solid border-gray-150'
      )}
    >
      <div className="flex justify-between items-center pt-[22px]">
        <SaveSearchButtonMobileContainer
          dispatchCreateSearch={dispatchCreateSearch}
          dispatchRemoveSavedSearch={dispatchRemoveSavedSearch}
          isMobile
          onSaveSearchModalOpen={onSaveSearchModalOpen}
          origin={`f-popup-${url ?? ''}`}
        />
        <div className="absolute right-1/2 top-[18px] translate-x-1/2">
          <a aria-label="Zumper" href="/" className="block h-[34px]">
            <ZumperFullDark size={34} />
          </a>
        </div>
        <HamburgerCloseButton
          aria-label={'open side menu'}
          color="#0a0f1c"
          onClick={handleHamburgerClick}
        />
      </div>
      {showZoe ? (
        <ZoeHeaderModal
          dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
          getAIAssistantSearchFilters={getAIAssistantSearchFilters}
          mixpanelEvent={mixpanelEvent}
          onSelectAutocompleteResult={onSelectAutocompleteResult}
          placeholder={locationString}
          variation={variation}
        />
      ) : (
        <Button
          aria-label="Search"
          onClick={onSearchButtonClick}
          variant="ghost"
          className="bg-gray-0 border border-solid border-gray-150 absolute bottom-0 translate-y-1/2 w-[calc(100%-48px)] h-12 shadow-[0_4px_12px_0_rgba(0,0,0,0.1)]"
        >
          <p className="truncate text-gray-800 font-medium text-base">{locationString}</p>
        </Button>
      )}
    </nav>
  )
}
